import { animation, constants } from '@config';
import Store from '@store';

import { handleTextShortcuts, shouldAvoidShortcuts } from '../../../helpers/shortcut-helpers';
import FormatFactory from '../../factory/formats';
import PageThumbnailFactory from '../../factory/page-thumbnail';
import * as downloadContent from '../top-submenu/contents/download/download-content-settings';
import DownloadStorageManage from '../top-submenu/contents/download/download-storage-manage';

import contentItem from './content-item.hbs';
import template from './template.hbs';

const itemHeight = 130; // px
let pagesLocalDB = [];
let restrictionsFactoryInitialized = false;

const getDesign = (pageNumber) => {
  return INKIVE.ProjectFacade.get('design', {
    page_number: pageNumber + 1 // page_number is one-indexed
  });
};

const initPagesLocalDB = async (num) => {
  const state = Store.getState();
  const db = [];

  for (let i = 0; i < num; i++) {
    const design = getDesign(i);

    if (design) { // TODO: remove this once we get rid of Mediator issues creating inconsistencies in pages
      const designId = design.design_id;
      const thumbnail = state.page[designId]?.thumbnail;

      db.push({
        imgSrc: thumbnail || '',
        designId
      });
    }
  }

  return db;
};

let currentPage = 0;

module.exports = Backbone.View.extend({
  events: {
    'click .btn-add-page:not(.disabled)': 'addPage',
    'click .btn-show-pages:not(.disabled)': 'togglePagesOpen',
    'click .btn-panel-header-close:not(.disabled)': 'setPagesClosed',
    'click .btn-zoom.custom:not(.disabled)': 'showCustomZoom',
    'click .custom-zoom:not(.disabled) a': 'customZoom',
    'click .btn-zoom.in:not(.disabled)': 'zoomInOut',
    'click .btn-zoom.out:not(.disabled)': 'zoomInOut',
    'click .item:not(.disabled)': 'changePage',
    'click .page-option:not(.disabled)': 'triggerPageOption',
    'click .btn-page-options:not(.disabled)': 'showPageOptions'
  },

  get isMobile () {
    return INKIVE.C.IS_MOBILE;
  },

  initialize () {
    if (this.isMobile) return;

    this.pageLocked = false;
    this.zoom = 'fit';

    const initializations = async () => {
      if (this.hasRestrictions()) {
        await INKIVE.RestrictionsFacade.initialize();
      }
      await PageThumbnailFactory.initialize();
      await FormatFactory.getCurrentFormat();
    };

    initializations().then(async () => {
      const printType = INKIVE.Helpers.getPrintGuidelinesProject();
      this.customBackground = constants.printingTypes[printType]?.thumb
        ? constants.printingTypes[printType]
        : null;
      this.render();
      this.addEventListeners();
      this.pagesLength = INKIVE.C.BOOK.num_pages;

      pagesLocalDB = await initPagesLocalDB(this.pagesLength);
      this.initList();
      this.update();

      if (INKIVE.RestrictionsFactory && !restrictionsFactoryInitialized) {
        await INKIVE.RestrictionsFactory.initialize();
        restrictionsFactoryInitialized = true;
      }
    });

    this.debouncedPageMoveTo = this.debounce(this.pageMoveTo, 100);

    INKIVE.EditorFacade.on('page.move_to', ({ page_number: pageNumber }) => this.setActivePage(pageNumber));

    PageThumbnailFactory.on('update', () => this.update());
    INKIVE.EditorFacade.on('page.saved', () => this.update());
    INKIVE.EditorFacade.on('page.load_fail', () => this.setActivePage(INKIVE.Tool._previous_page_number));
    INKIVE.EditorFacade.on('page.saving.error', () => this.setActivePage(INKIVE.Tool._previous_page_number));
    INKIVE.ProjectFacade.on('design.removed', () => this.update());
    INKIVE.ProjectFacade.on('design.copied', () => this.update());
    INKIVE.ProjectFacade.on('design.added', () => this.update());
    INKIVE.DesignFacade.on('template.update.designed', () => this.update());
    INKIVE.DesignFacade.on('template.update.restricted', () => this.update());
    INKIVE.DesignFacade.on('template.update.template_applied_on_current_page', () => this.update());
  },

  setActivePage (pageNumber) {
    this.pageLocked = false;
    currentPage = pageNumber ? pageNumber - 1 : 0;
    this.toggleSVGLoadingIndicator(false);
    this.updateActivePageStyles();
    this.el.querySelectorAll('.item').forEach((item) => item.classList.remove('disabled'));
  },

  render () {
    if (!this.isMobile) {
      this.$el.html(
        template({
          canAddPage: this.canAddPage(),
          customBackground: !!this.customBackground
        })
      );
    }
  },

  update () {
    this.pagesLength = INKIVE.C.BOOK.num_pages;

    const pageCount = this.el?.querySelectorAll('.page-count');
    if (pageCount && !this.customBackground) {
      pageCount.forEach(el => {
        el.innerHTML = `(${this.pagesLength})`;
      });
    }
    const zoomButton = this.el?.querySelector('.btn-zoom.custom');
    if (zoomButton) zoomButton.innerHTML = this.getZoomDisplayText();

    if (this.canChangePageOrder() && !this.sortableCreated) {
      INKIVE.Helpers.initTooltips(this.$el);
      const contentItemsList = this.$el.find('.content-items-list:not(.sortable-disabled)');
      if (contentItemsList && contentItemsList.length && !INKIVE.C.PRINTING_TYPE) {
        contentItemsList.sortable({
          axis: 'y',
          update: (e, ui) => {
            const index = ui.item.index();
            this.updatePageOrder(index);
          }
        });
        this.sortableCreated = true;
      }
    }

    initPagesLocalDB(this.pagesLength).then((db) => {
      pagesLocalDB = db;
      this.recycleDOM();
    });

    this.recycleDOM();
  },

  resetDOM () {
    initPagesLocalDB(this.pagesLength).then((db) => {
      pagesLocalDB = db;

      this.initList();
      this.update();
    });
  },

  async updatePageOrder (index) {
    const currentPageID = INKIVE.Tool._current_design_id.toString();
    const oldPage = currentPage;

    const items = this.el.querySelectorAll('.content-item-wrapper');
    const newOrder = [...items]
      .map((item) => {
        // Prevent incorrect updates by locking items until order uploaded
        item.classList.add('disabled');
        return item.dataset.designid;
      })
      .filter((i) => !!i);

    INKIVE.ProjectFacade.changePageOrder(newOrder, index, true).catch(() => {
      currentPage = oldPage;
      updateActivePage.call(this, currentPage);
      INKIVE.EditorFacade.canvas.call('advice.show', 're-order-pages-error');
    });

    currentPage = newOrder.indexOf(currentPageID);
    updateActivePage.call(this, currentPage);

    function updateActivePage (currentPageIndex) {
      this.resetDOM();
      const activePage = this.el.querySelector(`.content-item-wrapper[data-currpage="${currentPageIndex}"]`);
      if (!activePage) return;

      this.el.querySelectorAll('.content-item-wrapper').forEach((item) => delete item.dataset.active);
      activePage.dataset.active = true;
      this.el.querySelectorAll('.content-item-wrapper').forEach((item) => item.classList.remove('disabled'));
    }
  },

  createListItem ({ pageNumber, imgSrc, designId }) {
    const item = document.createElement('LI');
    let _pageNumber = pageNumber + 1; // page_number is one-indexed
    let footerTitle;

    if (this.customBackground) {
      footerTitle = INKIVE.TranslateHelper.translate(
        `bottom_submenu.pages.stationary_title.${this.customBackground.id}.${pageNumber}`);
      _pageNumber = null;
      item.classList.add('stationary-item');
    }

    item.dataset.currpage = pageNumber;
    item.dataset.designid = designId;
    item.classList.add('content-item-wrapper');
    item.setAttribute('id', `content-item-${pageNumber}`);
    item.innerHTML = contentItem({
      showAnimation: this.showAnimation(),
      canAddPage: this.canAddPage(),
      pageNumber: _pageNumber,
      imgSrc: '',
      footerTitle
    });
    // Refresh img
    if (imgSrc) item.querySelector('.preview').style.backgroundImage = `url(${imgSrc})`;
    if (this.customBackground) {
      item.querySelector(
        '.item').style.backgroundImage = `url(${this.customBackground.thumb[pageNumber].url})`;
    }

    this.customBackground && this._addCustomBackground(item, pageNumber);
    return item;
  },

  initList () {
    const itemsList = this.el.querySelector('.content-items-list');
    const end = itemsList.clientHeight / itemHeight;

    itemsList.innerHTML = '';

    for (let i = 0; i < this.pagesLength; i++) {
      const designId = pagesLocalDB[i] ? pagesLocalDB[i].designId : '';
      const imgSrc = pagesLocalDB[i] && i < Math.ceil(end) ? pagesLocalDB[i].imgSrc : '';

      const item = this.createListItem({
        pageNumber: i,
        imgSrc,
        designId
      });
      itemsList.appendChild(item);
    }

    const animationConfiguration = INKIVE.AnimationFactory.getAnimationConfiguration();
    if (animationConfiguration?.pages) {
      this.updateDuration(animationConfiguration.pages);
    }
  },

  recycleDOM () {
    // Recycles exiting dom elements, and adds new ones
    const itemsList = this.el.querySelector('.content-items-list');
    if (!itemsList) return;
    const firstIndex = itemsList.scrollTop / itemHeight;
    const visibleListLength = itemsList.clientHeight / itemHeight;
    const start = Math.floor(firstIndex);
    const end = Math.ceil(firstIndex + visibleListLength);

    // Add missing DOM elements
    let pageNumber = itemsList.querySelectorAll('li').length;
    while (pageNumber < this.pagesLength) {
      const designId = pagesLocalDB[pageNumber] ? pagesLocalDB[pageNumber].designId : '';
      const imgSrc = pagesLocalDB[pageNumber] && pageNumber < Math.ceil(end) ? pagesLocalDB[pageNumber].imgSrc : '';

      const item = this.createListItem({
        pageNumber,
        imgSrc,
        designId
      });
      itemsList.appendChild(item);
      item.classList.add('disabled');
      pageNumber++;
      if (this.sortableCreated) {
        this.$el.find('.content-items-list').sortable('refresh');
      }
    }

    for (let i = start; i < end; i++) {
      const item = itemsList.querySelector(`#content-item-${i}`);
      if (item) {
        item.classList.remove('disabled');

        if (pagesLocalDB[i]) {
          const designId = pagesLocalDB[i].designId;
          const imgSrc = pagesLocalDB[i].imgSrc;

          item.dataset.designid = designId;
          const preview = item.querySelector('.preview');
          if (preview && imgSrc) {
            preview.style.backgroundImage = `url(${imgSrc})`;
            preview.dataset.backgroundImage = `url(${imgSrc})`;
          }
        } else {
          // Update DOM to reflect page deletion
          item.remove();
        }
      }
    }

    this.updateActivePageStyles();

    const animationConfiguration = INKIVE.AnimationFactory.getAnimationConfiguration();
    if (animationConfiguration?.pages) {
      this.updateDuration(animationConfiguration.pages);
    }
  },

  attemptImageLoad (img, src, attempt = 0, maxAttempts = 1, attemptDelayInMs = 500) {
    let t;
    if (attempt > maxAttempts) {
      clearTimeout(t);
      return;
    }
    const imgLoader = new Image();
    imgLoader.src = src;
    imgLoader.onload = () => {
      img.style.backgroundImage = `url(${src})`;
      clearTimeout(t);
    };
    imgLoader.onerror = () => {
      t = setTimeout(() => {
        this.attemptImageLoad(img, src, attempt + 1, maxAttempts, attemptDelayInMs);
      }, attempt * attemptDelayInMs);
    };
  },

  addEventListeners () {
    const { editor } = INKIVE.Tool.getInstance();

    // Global listener for arrow-down and arrow-up keys to change page
    // Only changes page when an element is *not* selected, also when the editor is not in text-editing mode
    // Global listener for delete key to delete page

    document.addEventListener('keydown', (event) => {
      // Fix multiple keydown events triggering same page
      const target = event.target;
      const editorMode = editor.canvas.getMode();
      const inSelectMode = editorMode === 'select';
      const selectedItems = editor.canvas.getSelectedElems();
      const anElementIsSelected = selectedItems && selectedItems.length;
      const canChangeDecoration = anElementIsSelected &&
        INKIVE.RestrictionsFacade.isActionAllowed('text_decoration', selectedItems[0]);

      if (shouldAvoidShortcuts(target) || target.shadowRoot || this.pageLocked) return;
      else if (inSelectMode && anElementIsSelected) return;
      else if (editorMode === 'textedit') {
        handleTextShortcuts(event, canChangeDecoration);
        return;
      } else if (INKIVE.C.PRINTING_TYPE) {
        return;
      }

      const toLast =
        ['ArrowUp', 'Up', 'ArrowLeft'].includes(event.key) ||
        ((event.ctrlKey || event.metaKey) && event.keyCode === 188);
      const toFirst =
        ['ArrowDown', 'Down', 'ArrowRight'].includes(event.key) ||
        ((event.ctrlKey || event.metaKey) && event.keyCode === 190);

      if (toLast) {
        // Go to last page (circle around)
        const index = currentPage === 0 ? this.pagesLength - 1 : currentPage - 1;
        this.changePage(null, index);
      } else if (toFirst) {
        // Go to first page (circle around)
        const index = currentPage === this.pagesLength - 1 ? 0 : currentPage + 1;
        this.changePage(null, index);
      }
    });

    const onScroll = this.debounce(this.recycleDOM, 250);
    this.el.querySelector('.content-items-list').addEventListener('scroll', onScroll);

    this.el.querySelector('.content-items-wrapper > :first-child').addEventListener('scroll', this.removeActiveClass.bind(this));
    document.addEventListener('mouseup', this.removeActiveClass.bind(this));

    // Animation sidebar has updated the current page's properties
    document.addEventListener(
      'Animation.pageSetProperties',
      ({
        detail: {
          pageId,
          properties
        }
      }) => {
        this.updateDuration({ [pageId]: properties }, pageId);
      }
    );
  },

  updateDuration (pagesConfiguration, singlePageIdOverride) {
    const pageElements = [...document.querySelectorAll('.content-item-wrapper')];

    pageElements.forEach((pageElement, pageIndex) => {
      const isLastPage = pageIndex === pageElements.length - 1;
      const pageId = pageElement.dataset.designid;
      const durationElement = pageElement.querySelector('.duration');
      if (!durationElement) return;

      if (singlePageIdOverride && singlePageIdOverride.toString() !== pageId) return;

      const pageConfiguration = pagesConfiguration[pageId];
      const inOutDuration = isNaN(pageConfiguration?.inOutDuration)
        ? animation.defaultInOutDuration
        : pageConfiguration?.inOutDuration;

      const pageDuration = isNaN(pageConfiguration?.pageDuration)
        ? animation.defaultPageDuration
        : pageConfiguration?.pageDuration;

      let durationInMs = pageDuration + inOutDuration;
      if (!isLastPage) durationInMs += inOutDuration;

      const durationInSeconds = Math.round(durationInMs / 1000);
      durationElement.classList.remove('hidden');
      durationElement.innerHTML = `${durationInSeconds} sec`;
    });
  },

  removeActiveClass (e) {
    this.el.querySelectorAll('.control.active').forEach((c) => c.classList.remove('active'));
    this.el.querySelector('.page-options-popover')?.classList.remove('active');
  },

  setPagesOpen () {
    if (document.querySelector('[data-action=order-print]')?.classList.contains('.order-print-shown')) {
      return;
    }

    this.el.querySelector('.pages-panel')?.classList.add('pages-panel-opened');
    INKIVE.C.PAGES_PANEL_OPEN = true;

    this.changeVerticalScrollPositionX();
  },

  hideCustomZoom () {
    const zoomButton = this.el.querySelector('.custom-zoom');
    if (zoomButton) zoomButton.style.display = '';
  },

  updateActivePageStyles (toPage) {
    const pages = [...this.el.querySelectorAll('.content-item-wrapper[data-currpage]')];
    let activePage = this.el.querySelector(`.content-item-wrapper[data-currpage="${toPage || currentPage}"]`);
    if (!activePage) {
      activePage = pages[pages.length - 1];
    }

    this.el.querySelectorAll('.content-item-wrapper').forEach((i) => delete i.dataset.active);
    activePage.dataset.active = true;
    this.zoom = 'fit';
    document.querySelector('.zoom-wrapper .custom').textContent = this.getZoomDisplayText();
  },

  async addPage () {
    if (this.canAddPage()) {
      localStorage.setItem('pageActions', 'true');
      const pagesLength = ++this.pagesLength;

      await INKIVE.ProjectFacade.handle('design', 'add', {
        page_number: pagesLength,
        quiet: true
      });

      document.querySelector('.content-items-list >:last-child').scrollIntoView({ behavior: 'smooth' });
    }
  },

  togglePagesOpen () {
    if (!INKIVE.C.PAGES_PANEL_OPEN) {
      this.setPagesOpen();
      const skipButton = document.querySelector('pages-sidebar-story button[data-button-name="skip"]');
      skipButton && skipButton.click();
    }
  },

  setPagesClosed () {
    this.el.querySelector('.pages-panel')?.classList.remove('pages-panel-opened');
    INKIVE.C.PAGES_PANEL_OPEN = false;

    this.changeVerticalScrollPositionX();
  },

  showCustomZoom () {
    const zoomButton = this.el.querySelector('.custom-zoom');
    if (zoomButton) zoomButton.style.display = 'block';
  },

  customZoom (e) {
    const zoomButton = this.el.querySelector('.custom-zoom');
    if (zoomButton) zoomButton.style.display = 'none';

    this.zoom = e.currentTarget.dataset.value;

    if (this.zoom === 'fit') {
      INKIVE.EditorFacade.handle('zoom', 'fit');
    } else if (this.zoom === 'original') {
      INKIVE.EditorFacade.handle('zoom', 'original');
    } else {
      INKIVE.EditorFacade.handle('zoom', 'set', {
        zoom: this.zoom / 100
      });
    }
    document.querySelector('.zoom-wrapper .custom').textContent = this.getZoomDisplayText();
    this.changeVerticalScrollPositionX();
    $('#ik-editor').antiscroll();
    this.moveEditorHScrollUpOnRulerVisible();
  },

  zoomInOut (e) {
    INKIVE.EditorFacade.handle('zoom', e.currentTarget.classList.contains('in') ? 'in' : 'out');
    this.changeVerticalScrollPositionX();
    this.moveEditorHScrollUpOnRulerVisible();
  },

  moveEditorHScrollUpOnRulerVisible () {
    const horizontalRuler = document.querySelector('#ruler-horizontal');
    const horizontalScroll = document.querySelector('#ik-editor .antiscroll-scrollbar-horizontal');
    const isHidden = horizontalRuler.classList.contains('hidden');

    if (horizontalScroll) horizontalScroll.classList.toggle('display-ruler', !isHidden);
  },

  toggleSVGLoadingIndicator (bool) {
    const { editor } = INKIVE.Tool.getInstance();
    editor.loadingElement(bool);
  },

  changePage (e, index) {
    let activePage;

    if (e && e.currentTarget) {
      activePage = e.currentTarget.parentElement;
      const pageIsActive = activePage.dataset.active;
      if (pageIsActive) return;
    }

    const fileType = DownloadStorageManage.getDownloadFormat();

    if (downloadContent.selectedPages.length === 1 && (fileType === 'png' || fileType === 'jpg')) {
      DownloadStorageManage.setUrlValue();
      const pageNumber = Number(activePage.getAttribute('data-currpage')) + 1;
      downloadContent.selectedPages = [pageNumber];
      DownloadStorageManage.saveFieldOnStore([pageNumber], 'pages-to-dowload');
    }

    this.pageLocked = false;
    this.el.querySelectorAll('.item').forEach((item) => item.classList.add('disabled'));

    const toPage = typeof index === 'number' ? index : Number(activePage.dataset.currpage);
    currentPage = toPage;
    this.updateActivePageStyles(toPage);

    const toPageElement = this.el.querySelector(`.content-item-wrapper[data-currpage="${toPage}"]`);

    if (toPageElement && !this.inViewport(toPageElement)) {
      toPageElement.scrollIntoView();
    }
    FormatFactory.getCurrentFormat().then((format) => {
      if (format) {
        localStorage.setItem('currentFormatId', JSON.stringify(format.id));
      }
      this.debouncedPageMoveTo(toPage);
    });
  },

  pageMoveTo (toPage) {
    const shouldTogglePageLoader = INKIVE.hasEditorUnsavedChanges && !INKIVE.C.SHOW_TEXT_OVERFLOW;
    if (typeof this.pageLocked === 'boolean') {
      shouldTogglePageLoader && this.toggleSVGLoadingIndicator(true);
      this.pageLocked = INKIVE.Tool._current_page_number - 1;
      currentPage = toPage;
      INKIVE.EditorFacade.handle('page', 'move_to', {
        page_number: toPage + 1, // page_number is one-indexed
        quiet: true,
        save: true
      });
    } else {
      shouldTogglePageLoader && this.toggleSVGLoadingIndicator(false);
    }
  },

  triggerPageOption (e) {
    e.stopImmediatePropagation();

    const { action } = e.currentTarget.dataset;
    if (action === 'duplicate') {
      this.pagesLength++;
      INKIVE.ProjectFacade.handle('design', 'copy', {
        design_id: this.pageOptionsActivePageId,
        quiet: true
      });
    } else if (action === 'delete') {
      this.pagesLength--;
      INKIVE.ProjectFacade.handle('design', 'remove', {
        design_id: this.pageOptionsActivePageId,
        quiet: false
      });
    }
  },

  updateControlPopoverPos (item) {
    const control = item.querySelector('.control');
    const controlPopover = this.el.querySelector('.control-popover');

    // Position page options next to current item
    const controlRect = control.getBoundingClientRect();
    const controlPopoverRect = controlPopover.getBoundingClientRect();
    controlPopover.style.top = `${controlRect.top + controlRect.height}px`;
    controlPopover.style.left = `${controlRect.left - controlPopoverRect.width + controlRect.width}px`;
  },

  showPageOptions (e) {
    e.stopImmediatePropagation();

    const item = e.currentTarget.closest('.content-item-wrapper');
    const control = e.currentTarget.closest('.control');

    this.pageOptionsActivePageId = item.dataset.designid;

    this.removeActiveClass();
    control.classList.add('active');
    this.el.querySelector('.page-options-popover').classList.add('active');

    this.updateControlPopoverPos(item);
  },

  changeVerticalScrollPositionX: function () {
    const editorVerticalScroll = document.querySelector('#ik-editor > .antiscroll-scrollbar-vertical');
    if (editorVerticalScroll) {
      editorVerticalScroll.classList.toggle('vertical-scroll-open-panel', INKIVE.C.PAGES_PANEL_OPEN === true);
    }
  },

  inViewport (el) {
    if (!el || el.nodeType !== 1) {
      return false;
    }
    const boundingClientRect = el.getBoundingClientRect();
    const parent = this.el.querySelector('.content-items-list');

    return (
      !!boundingClientRect &&
      boundingClientRect.bottom - boundingClientRect.height * 2 >= 0 &&
      boundingClientRect.top - boundingClientRect.height <= parent.offsetHeight
    );
  },

  debounce (callback, wait) {
    let timeoutId = null;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        callback.apply(this, args);
      }, wait);
    };
  },

  getZoomDisplayText () {
    if (this.zoom === 'fit' || this.zoom === 'original') {
      return INKIVE.TranslateHelper.translate(`bottom_submenu.zoom.${this.zoom}`);
    } else {
      return `${this.zoom}%`;
    }
  },

  hasRestrictions () {
    return INKIVE.C.BOOK.book_type === INKIVE.C.BOOK_TYPE.RESTRICTED_DESIGN;
  },

  showAnimation () {
    return INKIVE.C.SHOW_ANIMATION &&
      INKIVE.RestrictionsFacade.isFunctionalityAllowed('function_animation_file');
  },

  canChangePageOrder () {
    return INKIVE.RestrictionsFacade && this.hasRestrictions()
      ? INKIVE.RestrictionsFacade.isFunctionalityAllowed('function_change_page_order')
      : true;
  },

  canAddPage () {
    if (INKIVE.C.PRINTING_TYPE && INKIVE.C.PRINTING_TYPE.max_pages === INKIVE.C.PRINTING_TYPE.min_pages) {
      return false;
    }
    return INKIVE.RestrictionsFacade && this.hasRestrictions()
      ? INKIVE.RestrictionsFacade.isFunctionalityAllowed('function_add_page')
      : true;
  },

  _addCustomBackground (item, pageNumber) {
    INKIVE.PageFactory.getProjectData().then((data) => {
      const previewItem = item.querySelector('.preview-item');
      const thumb = this.customBackground.thumb[pageNumber];
      const imgWidth = thumb.width;
      const imgHeight = thumb.height;
      const bgWidth = 220;
      const bgHeight = 330;

      item.style.width = `${bgWidth}px`;
      item.style.height = `${bgHeight}px`;
      previewItem.style.width = `${imgWidth}px`;
      previewItem.style.height = `${imgHeight}px`;
      previewItem.style.marginTop = thumb.margin;
      previewItem.querySelector('.preview').classList.add('custom-background');
      item.querySelector('.item').style.backgroundSize = thumb.size;
      if (thumb.hidden) {
        item.style.display = 'none';
      }
    });
  }
});
